import { apiRequest } from '../apiClient';

// Types for the API response
export interface IssuerDashboardStats {
  total_credentials: number;
  active_credentials: number;
  total_verifications: number;
  active_schemas: number;
  total_templates: number;
  this_month_credentials: number;
  verification_success_rate: number;
  avg_response_time: number;
  monthly_growth: number;
}

export interface IssuerCredentialSummary {
  id: string;
  title: string;
  recipient: string;
  recipient_email: string;
  issued_date: string;
  status: string;
  verification_count: number;
  category: string;
  schema_name: string;
}

export interface IssuerSchemaSummary {
  id: string;
  name: string;
  version: string;
  description: string;
  field_count: number;
  required_field_count: number;
  is_active: boolean;
  created_at: string;
  credential_count: number;
}

export interface IssuerAnalyticsResponse {
  organization_id: string;
  organization_name: string;
  stats: IssuerDashboardStats;
  credential_trends: any[];
  verifications_by_type: any[];
  top_verifiers: any[];
  last_updated: string;
}

export interface IssuerDashboardData {
  organization_id: string;
  organization_name: string;
  analytics: IssuerAnalyticsResponse;
  credentials: IssuerCredentialSummary[];
  schemas: IssuerSchemaSummary[];
  last_updated: string;
}

export interface IssuerDashboardResponse {
  success: boolean;
  data: IssuerDashboardData;
  message: string;
}

export interface IssuerCredentialsResponse {
  success: boolean;
  data: IssuerCredentialSummary[];
  message: string;
}

export interface IssuerSchemasResponse {
  success: boolean;
  data: IssuerSchemaSummary[];
  message: string;
}

// Service class for issuer analytics
export class IssuerAnalyticsService {
  private static readonly BASE_URL = '/api/issuer';

  /**
   * Get complete issuer dashboard data
   */
  static async getDashboardData(limit: number = 10): Promise<IssuerDashboardData> {
    try {
      const response = await apiRequest<IssuerDashboardResponse>(
        `${this.BASE_URL}/dashboard?limit=${limit}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching issuer dashboard data:', error);
      throw error;
    }
  }

  /**
   * Get analytics data only
   */
  static async getAnalytics(): Promise<IssuerAnalyticsResponse> {
    try {
      const response = await apiRequest<IssuerAnalyticsResponse>(
        `${this.BASE_URL}/analytics`
      );
      return response;
    } catch (error) {
      console.error('Error fetching issuer analytics:', error);
      throw error;
    }
  }

  /**
   * Get credentials list
   */
  static async getCredentials(
    page: number = 1,
    limit: number = 10,
    status?: string,
    schema_id?: string
  ): Promise<IssuerCredentialSummary[]> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (status) params.append('status', status);
      if (schema_id) params.append('schema_id', schema_id);

      const response = await apiRequest<IssuerCredentialsResponse>(
        `${this.BASE_URL}/credentials?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching issuer credentials:', error);
      throw error;
    }
  }

  /**
   * Get schemas list
   */
  static async getSchemas(
    page: number = 1,
    limit: number = 10,
    is_active?: boolean
  ): Promise<IssuerSchemaSummary[]> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (is_active !== undefined) params.append('is_active', is_active.toString());

      const response = await apiRequest<IssuerSchemasResponse>(
        `${this.BASE_URL}/schemas?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching issuer schemas:', error);
      throw error;
    }
  }
}

export default IssuerAnalyticsService; 